// Shield Effect - 機体別エネルギーバリアエフェクト
// 全描画を drawingContext (Canvas 2D API) で実行

import { getP5 } from '../../../core/p5-instance.js';
import { getShieldTimer, getSelectedShip } from '../../../store/index.js';
import { CONFIG } from '../../../config/game-config.js';

// === 型定義 ===

interface ShieldContext {
    ctx: CanvasRenderingContext2D;
    fc: number;
    radius: number;
    r: number;
    g: number;
    b: number;
    isWarning: boolean;
    timeRatio: number;
    flickerMul: number;
    pulsePeriod: number;
}

// === HUNTER用: 密な六角形タイリング座標の事前計算 ===

interface DenseHexCell {
    x: number;
    y: number;
    dist: number;
    qr: number;  // セル固有シード (q*7 + r*13)
}

const DENSE_HEX_CELLS: DenseHexCell[] = [];
const DOME_HEX_SIZE = 0.14;

const hexSpacingX = Math.sqrt(3) * DOME_HEX_SIZE;
const hexSpacingY = 1.5 * DOME_HEX_SIZE;
const gridRange = 5;

for (let q = -gridRange; q <= gridRange; q++) {
    for (let r = -gridRange; r <= gridRange; r++) {
        const x = hexSpacingX * (q + r * 0.5);
        const y = hexSpacingY * r;
        const dist = Math.sqrt(x * x + y * y);
        if (dist < 0.92) {
            DENSE_HEX_CELLS.push({ x, y, dist, qr: q * 7 + r * 13 });
        }
    }
}

// === LANCER用: 電撃アークテンプレートの事前計算 ===

interface ArcTemplate {
    startAngle: number;
    segmentCount: number;
    seed: number;
    inwardDepth: number;
}

const ARC_TEMPLATES: ArcTemplate[] = [];
for (let i = 0; i < 6; i++) {
    ARC_TEMPLATES.push({
        startAngle: (i / 6) * Math.PI * 2,
        segmentCount: 5 + (i % 3),
        seed: i * 17 + 3,
        inwardDepth: 0.3 + (i % 4) * 0.15
    });
}

// === ヘルパー関数 ===

/**
 * 六角形パスを描画（fill + stroke）
 */
function drawHexagonPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, hexRadius: number): void {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2 - Math.PI / 6;
        const hx = cx + Math.cos(angle) * hexRadius;
        const hy = cy + Math.sin(angle) * hexRadius;
        if (i === 0) {
            ctx.moveTo(hx, hy);
        } else {
            ctx.lineTo(hx, hy);
        }
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
}

// === 共通コンテキスト計算 ===

function computeShieldContext(size: number): ShieldContext {
    const p = getP5();
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const fc = p.frameCount;
    const radius = size * 1.0;

    const ship = getSelectedShip();
    const baseColor = CONFIG.ships[ship].color;
    let r = baseColor[0];
    let g = baseColor[1];
    let b = baseColor[2];

    const shieldDuration = CONFIG.ships[ship].shieldDuration;
    const timeRatio = getShieldTimer() / shieldDuration;
    const isWarning = timeRatio <= 0.2;

    if (isWarning) {
        const warningBlend = 1 - (timeRatio / 0.2);
        r = Math.round(r + (255 - r) * warningBlend * 0.6);
        g = Math.round(g * (1 - warningBlend * 0.7));
        b = Math.round(b * (1 - warningBlend * 0.7));
    }

    let flickerMul = 1;
    if (isWarning) {
        const flicker = Math.sin(fc * 0.8) + Math.sin(fc * 1.3);
        flickerMul = 0.5 + (flicker + 2) / 4 * 0.5;
    }

    const pulsePeriod = isWarning ? 90 / (1 + 2 * (1 - timeRatio / 0.2)) : 90;

    return { ctx, fc, radius, r, g, b, isWarning, timeRatio, flickerMul, pulsePeriod };
}

// === 共通レイヤー関数 ===

/** Layer: 外側グローオーラ（全機体共通） */
function drawGlowAura(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, flickerMul } = sc;
    const pulse = (Math.sin(fc * 0.05) * 0.3 + 0.7) * flickerMul;

    ctx.save();
    const glow = ctx.createRadialGradient(0, 0, radius * 0.3, 0, 0, radius);
    glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${(0.12 * pulse).toFixed(3)})`);
    glow.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/** Layer: エネルギーサーフェス — フレネル半透明ドーム（BOMBER用） */
function drawEnergySurface(sc: ShieldContext): void {
    const { ctx, radius, r, g, b, flickerMul } = sc;
    const a = flickerMul;

    ctx.save();
    const surface = ctx.createRadialGradient(0, 0, radius * 0.1, 0, 0, radius * 0.95);
    surface.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${(0.02 * a).toFixed(3)})`);
    surface.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${(0.05 * a).toFixed(3)})`);
    surface.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${(0.15 * a).toFixed(3)})`);
    ctx.fillStyle = surface;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.95, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/** Layer: パルス/衝撃波リング（BOMBER, HUNTER用） */
function drawPulseRings(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, flickerMul, pulsePeriod } = sc;

    for (let i = 0; i < 2; i++) {
        const offset = i * (pulsePeriod / 2);
        const t = ((fc + offset) % pulsePeriod) / pulsePeriod;
        const pulseRadius = radius * (0.3 + t * 0.8);
        const alpha = (1 - t) * 0.5 * flickerMul;

        if (alpha > 0.01) {
            ctx.save();
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
            ctx.lineWidth = 2 * (1 - t) + 0.5;
            ctx.beginPath();
            ctx.arc(0, 0, pulseRadius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
        }
    }
}

/** Layer: エッジハイライトリム（全機体共通） */
function drawEdgeRim(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, flickerMul } = sc;
    const rimPulse = (Math.sin(fc * 0.1) * 0.2 + 0.8) * flickerMul;

    ctx.save();
    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.8)`;
    ctx.shadowBlur = 15;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${(rimPulse * 0.7).toFixed(3)})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.95, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = `rgba(255, 255, 255, ${(rimPulse * 0.25).toFixed(3)})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
}

// === BOMBER: エネルギーシールド ===

function drawBomberShield(sc: ShieldContext): void {
    drawGlowAura(sc);
    drawEnergySurface(sc);
    drawPulseRings(sc);
    drawEdgeRim(sc);
}

// === HUNTER: ヘックスドーム ===

/** HUNTER固有: 密な六角形タイリングドーム */
function drawHexDome(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, isWarning, flickerMul } = sc;
    const hexSize = radius * DOME_HEX_SIZE;

    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.95, 0, Math.PI * 2);
    ctx.clip();

    const rotation = fc * 0.002;
    const cosR = Math.cos(rotation);
    const sinR = Math.sin(rotation);

    for (const hex of DENSE_HEX_CELLS) {
        const hx = hex.x * radius;
        const hy = hex.y * radius;
        const rx = hx * cosR - hy * sinR;
        const ry = hx * sinR + hy * cosR;

        // シマー波: 角度ベースの明滅が走る
        const angle = Math.atan2(hy, hx);
        const wave = Math.sin(angle * 2 + fc * 0.06 + hex.dist * 3);
        const shimmer = 0.3 + wave * 0.35;

        let cellAlpha = shimmer * flickerMul;

        // 警告時: セル個別にON/OFF
        if (isWarning) {
            const flick = Math.sin(fc * 0.5 + hex.qr * 2.7);
            if (flick < -0.3) {
                cellAlpha *= 0.1;
            }
        }

        // フレネル: 端ほど若干明るい（ドーム曲面感）
        const fresnel = 0.5 + hex.dist * 0.5;
        const finalAlpha = cellAlpha * fresnel;

        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${(finalAlpha * 0.25).toFixed(3)})`;
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${(finalAlpha * 0.6).toFixed(3)})`;
        ctx.lineWidth = 1.2;

        drawHexagonPath(ctx, rx, ry, hexSize);
    }

    ctx.restore();
}

function drawHunterShield(sc: ShieldContext): void {
    drawGlowAura(sc);
    drawHexDome(sc);
    drawEdgeRim(sc);
}

// === LANCER: プラズマアーク ===

/** LANCER固有: 不安定なプラズマ膜 */
function drawPlasmaMembrane(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, isWarning, flickerMul } = sc;

    const baseAlpha = isWarning ? 0.02 : 0.04;
    const edgeAlpha = isWarning ? 0.08 : 0.18;

    // カオス脈動（複数sin重ね合わせ）
    const chaos = Math.sin(fc * 0.07) * 0.3
               + Math.sin(fc * 0.13) * 0.2
               + Math.sin(fc * 0.19) * 0.1;
    const chaosMul = (1 + chaos) * flickerMul;

    ctx.save();
    const membrane = ctx.createRadialGradient(0, 0, radius * 0.05, 0, 0, radius * 0.95);
    membrane.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${(baseAlpha * chaosMul).toFixed(3)})`);
    membrane.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${(0.06 * chaosMul).toFixed(3)})`);
    membrane.addColorStop(0.85, `rgba(${r}, ${g}, ${b}, ${(edgeAlpha * chaosMul).toFixed(3)})`);
    membrane.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${(0.05 * chaosMul).toFixed(3)})`);
    ctx.fillStyle = membrane;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.95, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/** LANCER固有: 電撃アーク（決定論的稲妻） */
function drawElectricArcs(sc: ShieldContext): void {
    const { ctx, fc, radius, r, g, b, isWarning, flickerMul } = sc;
    const activeCount = isWarning ? 5 : 3;
    const arcLifecycle = 25;

    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.98, 0, Math.PI * 2);
    ctx.clip();

    for (let a = 0; a < activeCount; a++) {
        const template = ARC_TEMPLATES[a % ARC_TEMPLATES.length];

        // アーク位置をフレームベースで巡回
        const cycleOffset = Math.floor(fc / arcLifecycle) * 2.3 + a * 1.7;
        const currentAngle = template.startAngle + Math.sin(cycleOffset) * Math.PI;

        // ライフサイクル内の経過（0~1）
        const age = (fc % arcLifecycle) / arcLifecycle;
        const arcAlpha = Math.sin(age * Math.PI) * 0.8 * flickerMul;
        if (arcAlpha < 0.05) {
            continue;
        }

        // 稲妻パスの始点と終点
        const startX = Math.cos(currentAngle) * radius * 0.9;
        const startY = Math.sin(currentAngle) * radius * 0.9;
        const endX = Math.cos(currentAngle + Math.PI) * radius * template.inwardDepth;
        const endY = Math.sin(currentAngle + Math.PI) * radius * template.inwardDepth;

        // 折れ線パス生成
        ctx.beginPath();
        ctx.moveTo(startX, startY);

        const perpAngle = currentAngle + Math.PI / 2;
        for (let s = 1; s <= template.segmentCount; s++) {
            const t = s / template.segmentCount;
            const bx = startX + (endX - startX) * t;
            const by = startY + (endY - startY) * t;

            const jitter = Math.sin(fc * 0.3 + template.seed * s + s * 7.1) * radius * 0.12
                         + Math.sin(fc * 0.7 + s * 13.3) * radius * 0.06;
            const jx = bx + Math.cos(perpAngle) * jitter;
            const jy = by + Math.sin(perpAngle) * jitter;

            ctx.lineTo(jx, jy);
        }

        // メインアーク（機体色 + グロー）
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${arcAlpha.toFixed(3)})`;
        ctx.lineWidth = isWarning ? 2.5 : 1.8;
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${(arcAlpha * 0.6).toFixed(3)})`;
        ctx.shadowBlur = 8;
        ctx.stroke();

        // 白コアストローク（細い）
        ctx.strokeStyle = `rgba(255, 255, 255, ${(arcAlpha * 0.5).toFixed(3)})`;
        ctx.lineWidth = 0.8;
        ctx.shadowBlur = 0;
        ctx.stroke();
    }

    ctx.shadowBlur = 0;
    ctx.restore();
}

function drawLancerShield(sc: ShieldContext): void {
    drawGlowAura(sc);
    drawPlasmaMembrane(sc);
    drawElectricArcs(sc);
    drawEdgeRim(sc);
}

// === 公開API ===

/**
 * drawEnergyBarrier() - 機体別エネルギーバリアエフェクト
 * @param size - プレイヤーサイズ
 */
export function drawEnergyBarrier(size: number): void {
    const sc = computeShieldContext(size);
    switch (getSelectedShip()) {
        case 'bomber':
            drawBomberShield(sc);
            break;
        case 'hunter':
            drawHunterShield(sc);
            break;
        case 'lancer':
            drawLancerShield(sc);
            break;
    }
}